import { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';

export function InstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showBanner, setShowBanner] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);

    useEffect(() => {
        // Check if already installed
        if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
            setIsInstalled(true);
            return;
        }

        // Check if user dismissed recently
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        if (dismissed) {
            const dismissedAt = parseInt(dismissed, 10);
            if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return; // 7 days
        }

        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            // Show banner after a short delay (let user interact first)
            setTimeout(() => setShowBanner(true), 5000);
        };

        window.addEventListener('beforeinstallprompt', handler);

        // iOS: show custom banner since iOS doesn't fire beforeinstallprompt
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        if (isIOS && !window.navigator.standalone) {
            setTimeout(() => setShowBanner(true), 5000);
        }

        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstall = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setIsInstalled(true);
            }
            setDeferredPrompt(null);
        }
        setShowBanner(false);
    };

    const handleDismiss = () => {
        setShowBanner(false);
        localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    };

    if (isInstalled || !showBanner) return null;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    return (
        <div className="fixed top-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 animate-fade-in" data-testid="install-prompt">
            <div className="bg-[#121620] border border-gold/30 rounded-2xl p-4 shadow-2xl shadow-black/50">
                <button
                    onClick={handleDismiss}
                    className="absolute top-3 right-3 p-1 text-white/30 hover:text-white/60 transition-colors"
                    data-testid="dismiss-install"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center shrink-0">
                        <Download className="w-6 h-6 text-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-white font-semibold text-sm mb-0.5">Install Voxoora</h3>
                        <p className="text-muted-foreground text-xs leading-relaxed mb-3">
                            {isIOS
                                ? <>Tap the <span className="inline-flex items-center px-1.5 py-0.5 bg-white/10 rounded text-white text-[10px] font-medium mx-0.5">Share <svg className="w-3 h-3 ml-0.5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg></span> button below, then select <strong className="text-white">"Add to Home Screen"</strong></>
                                : 'Add to your home screen for offline listening and a native app experience.'
                            }
                        </p>
                        {!isIOS && deferredPrompt && (
                            <button
                                onClick={handleInstall}
                                className="w-full py-2 rounded-lg bg-gold text-[#0A0D14] text-xs font-semibold hover:bg-gold/90 transition-colors active:scale-[0.98]"
                                data-testid="install-btn"
                            >
                                Install App
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
